import { useEffect, useRef, useState } from "react";
import { fetchMeets } from "@/lib/strapi";

export function useMeets(limit = 12, step = 12, country = "All") {
  const [meets, setMeets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [currentLimit, setCurrentLimit] = useState(limit);
  const [queryVersion, setQueryVersion] = useState(0);
  const previousCountryRef = useRef(country);

  useEffect(() => {
    if (previousCountryRef.current === country) {
      return;
    }

    previousCountryRef.current = country;
    setCurrentLimit(limit);
    setQueryVersion((value) => value + 1);
  }, [country, limit]);

  useEffect(() => {
    let mounted = true;

    const loadMeets = async () => {
      try {
        const loadingMore = currentLimit > limit;
        if (loadingMore) {
          setIsLoadingMore(true);
        } else {
          setIsLoading(true);
        }
        setError("");
        const data = await fetchMeets({ limit: currentLimit, country });
        if (mounted) {
          setMeets(data);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : "Failed to load meets");
          setMeets([]);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    };

    loadMeets();

    return () => {
      mounted = false;
    };
  }, [currentLimit, country, limit, queryVersion]);

  const loadMore = () => {
    if (isLoading || isLoadingMore) {
      return;
    }

    setCurrentLimit((value) => value + step);
  };

  const hasMore = meets.length === currentLimit;

  return { meets, isLoading, isLoadingMore, error, hasMore, loadMore };
}
